import { Box, Typography } from '@mui/material';
import React from 'react';
import { PageHeader } from './PageHeader';
/**
 * Renders a page header with an optional image, subtitle, and pre-title content.
 * Uses a gradient background and flexible layout to accommodate different screen sizes.
 *
 * @param {PageHeaderWithImageProps} root0 - Component props.
 * @param {Gradient} root0.gradient - Background gradient used in the header.
 * @param {React.ReactNode} root0.title - The main title of the header.
 * @param {React.ReactNode} [root0.preTitleContent] - Optional content displayed above the title.
 * @param {React.ReactNode} [root0.subtitle] - Optional subtitle displayed below the title.
 * @param {string} [root0.image] - Optional image URL to display on the right side.
 * @param {string} [root0.imageAlt] - Alternative text for the image.
 * @param {React.ReactNode} [root0.children] - Additional content displayed below the subtitle.
 * @returns {JSX.Element} The composed header section with image and text.
 */
export const PageHeaderWithImage = ({ gradient, title, preTitleContent, subtitle, image, imageAlt, children, }) => {
    return (React.createElement(PageHeader, { gradient: gradient },
        React.createElement(Box, { sx: {
                display: 'flex',
                flexDirection: 'column',
                boxSizing: 'border-box',
                flexGrow: 1,
                pl: { xs: 2, sm: 4, xl: 5 },
                pr: { xs: 2, sm: 4, lg: 0 },
                py: { xs: 2, sm: 4 },
                pt: { sm: 3 },
                rowGap: 2,
                width: '100%',
            } },
            preTitleContent != null && React.createElement(Box, null, preTitleContent),
            React.createElement(Typography, { variant: 'h1', "data-test": 'header-title' }, title),
            subtitle && (React.createElement(Typography, { sx: { typography: { xs: 'body2', sm: 'body1' } }, "data-test": 'header-subtitle' }, subtitle)),
            children),
        image && (React.createElement(Box, { sx: {
                alignItems: 'flex-end',
                display: 'flex',
                flexGrow: 1,
                justifyContent: 'center',
                overflow: 'hidden',
                width: '100%',
            }, "data-test": 'header-image' },
            React.createElement(Box, { component: 'img', src: image, alt: imageAlt, sx: { maxWidth: '100%' } })))));
};
